<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import OrgLogo from '@/components/OrgLogo.vue'
import { useLocalizedString } from '@/composables/useLocalizedString'
import { useOrgStore } from '@/stores/org'

const { t } = useI18n()
const router = useRouter()
const orgStore = useOrgStore()
const { resolve } = useLocalizedString()

const isOpen = ref(false)

// App info from org config
const appInfo = computed(() => ({
  version: __APP_VERSION__,
  buildDate: __BUILD_DATE__,
  orgName: resolve(orgStore.branding.appNameShort),
  orgFullName: resolve(orgStore.branding.appName),
  tagline: resolve(orgStore.branding.tagline),
}))

function goToAbout() {
  isOpen.value = false
  router.push('/about')
}
</script>

<template>
  <div class="info-badge-wrapper">
    <v-menu
      v-model="isOpen"
      location="top end"
      :close-on-content-click="false"
      offset="8"
    >
      <template #activator="{ props }">
        <button
          v-bind="props"
          class="info-badge"
          :class="{ 'is-open': isOpen }"
          :aria-label="t('badge.info')"
        >
          <span class="badge-version">v{{ appInfo.version }}</span>
        </button>
      </template>

      <div class="info-popup">
        <div class="info-popup-header">
          <OrgLogo size="md" />
          <div class="info-title">
            <h4>{{ appInfo.orgFullName }}</h4>
            <span v-if="appInfo.tagline" class="info-tagline">{{ appInfo.tagline }}</span>
          </div>
        </div>

        <v-divider class="my-3" />

        <div class="info-details">
          <div class="info-row">
            <v-icon icon="mdi-domain" size="18" />
            <div>
              <span class="info-label">{{ t('badge.organization') }}</span>
              <span class="info-value">{{ appInfo.orgName }}</span>
            </div>
          </div>

          <div class="info-row">
            <v-icon icon="mdi-tag-outline" size="18" />
            <div>
              <span class="info-label">{{ t('badge.version') }}</span>
              <span class="info-value">{{ appInfo.version }}</span>
            </div>
          </div>

          <div class="info-row">
            <v-icon icon="mdi-calendar-outline" size="18" />
            <div>
              <span class="info-label">{{ t('badge.build') }}</span>
              <span class="info-value">{{ appInfo.buildDate }}</span>
            </div>
          </div>
        </div>

        <v-divider class="my-3" />

        <div class="info-actions">
          <v-btn
            variant="tonal"
            color="primary"
            size="small"
            block
            @click="goToAbout"
          >
            <v-icon start icon="mdi-information-outline" />
            {{ t('badge.aboutPortal') }}
          </v-btn>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<style lang="scss" scoped>
.info-badge-wrapper {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

.info-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;

  // Glassmorphism - использует цвета темы организации
  background: color-mix(in srgb, var(--tlh-primary) 15%, transparent);
  backdrop-filter: blur(10px);
  border: 1px solid color-mix(in srgb, var(--tlh-primary) 20%, transparent);
  color: rgb(var(--v-theme-on-surface));

  .v-theme--glassDark & {
    background: color-mix(in srgb, var(--tlh-primary) 20%, transparent);
    border-color: color-mix(in srgb, var(--tlh-primary) 30%, transparent);
  }

  &:hover {
    background: color-mix(in srgb, var(--tlh-primary) 25%, transparent);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px color-mix(in srgb, var(--tlh-primary) 20%, transparent);
  }

  &.is-open {
    background: color-mix(in srgb, var(--tlh-primary) 30%, transparent);
  }
}

.badge-version {
  opacity: 0.9;
}

.info-popup {
  min-width: 280px;
  padding: 16px;
  border-radius: 12px;

  // Glassmorphism
  background: rgba(var(--v-theme-surface), 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);

  .v-theme--glassDark & {
    background: rgba(30, 41, 59, 0.95);
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
}

.info-popup-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.info-title {
  h4 {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0;
    line-height: 1.2;
  }
}

.info-tagline {
  font-size: 0.7rem;
  opacity: 0.6;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.info-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;

  .v-icon {
    opacity: 0.6;
    margin-top: 2px;
  }

  > div {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
}

.info-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.5;
}

.info-value {
  font-size: 0.85rem;
  font-weight: 500;
}

.info-actions {
  .v-btn {
    text-transform: none;
    font-weight: 500;
  }
}
</style>
